//#region Imports

import {HttpInterceptorFn} from '@angular/common/http';
import {inject} from '@angular/core';
import {Router} from '@angular/router';
import {catchError, switchMap, throwError} from 'rxjs';
import {MatSnackBar} from '@angular/material/snack-bar';

// @ew
import {RouteName} from '@ew/enums';
import {AuthHttpService} from '@ew/auth-http';
import {EW_TOASTER} from '@ew/ew-injects';
import {ToasterType} from '@ew/ew-services-interfaces';

//#endregion

/**
 * Whitelist of URLs that do not require JWT authentication.
 *
 * These endpoints are publicly accessible or use alternative authentication methods:
 * - Translation files (i18n)
 * - Public authentication endpoints (login, signup, password reset)
 * - Public tracking endpoints (for customers to track their service items)
 * - Staff-specific endpoints (use their own staffToken authentication)
 * - Chat endpoints (validated through payload rather than JWT header)
 */
const whiteListWs = [
  'i18n/en.json',
  'i18n/sr.json',
  'api/users/login',
  'api/users/sign-up',
  'api/users/verify',
  'api/users/resend-verification-code',
  'api/users/reset-password-request',
  'api/users/reset-password',
  'api/users/refresh-token',
  'api/users/resend-verification-code-email',
  'api/tracking-service-item/track/',
  'api/staff/', // Staff endpoints use their own authentication
  'upload/staff/', // Staff photo uploads use their own authentication
  'api/chat/get-staff-conversations', // Staff chat - auth by staffId
  'api/chat/get-staff-messages', // Staff chat - auth by staffId
  'api/chat/create-message', // Chat message creation - sender validated in payload
  'api/chat/mark-as-read', // Mark messages as read - userId validated in payload
];

/**
 * JWT HTTP Interceptor - Automatically adds authentication tokens to HTTP requests.
 *
 * This functional HTTP interceptor handles JWT token management for the entire application.
 * It provides the following features:
 *
 * 1. **Automatic Token Injection**: Adds Bearer token to Authorization header for all
 *    requests except those in the whitelist.
 *
 * 2. **Dual Token Support**: Handles both regular user tokens and staff tokens based on
 *    the current route (staff pages use staffToken, others use regular token).
 *
 * 3. **Automatic Token Refresh**: When a request fails with 401 (Unauthorized), it:
 *    - Attempts to refresh the expired token
 *    - Retries the original request with the new token
 *    - Redirects to login if refresh fails
 *
 * 4. **Whitelist Support**: Skips token injection for public endpoints like login,
 *    signup, and password reset.
 *
 * 5. **Error Handling**: Provides user-friendly error messages and automatic redirect
 *    when authentication fails.
 *
 * @example
 * // In app.config.ts or main.ts
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideHttpClient(
 *       withInterceptors([jwtInterceptor])
 *     )
 *   ]
 * };
 *
 * @param {HttpRequest<unknown>} req - The outgoing HTTP request
 * @param {HttpHandlerFn} next - The next handler in the interceptor chain
 * @returns {Observable<HttpEvent<unknown>>} Observable of the HTTP response
 *
 * @see whiteListWs - List of URLs that don't require authentication
 * @see AuthHttpService - Service used for token refresh
 */
export const jwtInterceptor: HttpInterceptorFn = (req, next) => {
  const authHttp = inject(AuthHttpService);
  const router = inject(Router);
  const toaster = inject(EW_TOASTER);

  // Check if we're on a staff page and use staffToken, otherwise use regular token
  const isStaffPage = window.location.pathname.startsWith('/staff');
  const authToken = isStaffPage
    ? localStorage.getItem('staffToken')
    : localStorage.getItem('token');

  // Skip if request is in the white list
  if (isRequestUrlInWhitelist(req.url)) {
    return next(req);
  }

  if (!authToken) {
    // Only redirect to login if we're not on a staff page
    if (!isStaffPage) {
      router.navigateByUrl(RouteName.Login);
    } else {
      router.navigate(['/staff/login']);
    }
    return next(req);
  }

  // Clone the request and add the authorization header
  const authReq = req.clone({
    setHeaders: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  // Pass the cloned request with the updated header to the next handler
  return next(authReq).pipe(
    catchError((error) => {
      // handle 401 errors
      if (authToken && error.status === 401) {
        return authHttp.refreshToken(authToken).pipe(
          switchMap((newToken) => {
            // Minimal shape check so a broken refresh response (empty
            // body, HTML error page leaked through, etc.) doesn't get
            // stored as the new access token and poison every future
            // request. A real JWT is three base64url segments split by
            // dots; we don't re-verify the signature client-side.
            const candidate = newToken?.accessToken;
            if (
              !candidate ||
              typeof candidate !== 'string' ||
              candidate.split('.').length !== 3
            ) {
              router.navigateByUrl(RouteName.Login);
              toaster.displayToast(
                'Your session has expired. Please log in again.',
                ToasterType.Error,
              );
              return throwError(() => new Error('Invalid refresh token response'));
            }

            // Save the validated token
            localStorage.setItem('token', candidate);

            // Retry the failed request with the new token
            const newAuthReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${candidate}`,
              },
            });

            return next(newAuthReq);
          }),
          catchError((refreshError) => {
            // If refresh fails, redirect to login
            router.navigateByUrl(RouteName.Login);
            toaster.displayToast(
              'Your session has expired. Please log in again.',
              ToasterType.Error,
            );

            return throwError(() => refreshError);
          }),
        );
      }

      return throwError(() => error);
    }),
  );
};

/**
 * Checks if the given request URL is in the whitelist.
 *
 * Performs exact endpoint matching to prevent false positives.
 * For example, 'api/users/verify' will match '/api/users/verify' or '/api/users/verify?code=123'
 * but will NOT match '/api/users/verify-email-change'.
 *
 * @param {string} request - the URL to check
 * @return {boolean} true if the request URL is in the whitelist, false otherwise
 */
export function isRequestUrlInWhitelist(request: string): boolean {
  return whiteListWs.some((address) => {
    // For API endpoints, ensure we match the exact endpoint path
    // by checking if the address is followed by end of string, /, or ?
    const index = request.indexOf(address);
    if (index === -1) return false;

    // Get the character after the matched address
    const charAfter = request[index + address.length];

    // Match if: end of string, followed by /, or followed by ?
    return charAfter === undefined || charAfter === '/' || charAfter === '?';
  });
}
